import { useCallback, useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
    CircularProgress, Alert, LinearProgress,
} from '@mui/material';
import { getCustomerCandidates, createReferrersFromCustomers } from '../../api/referralLinkApi';

/**
 * "Add from customers" — every customer who bought from us becomes someone who can
 * introduce the next one.
 * (user 2026-10-31: "all old customer got referral, u all can share them referral link or
 *  give them login")
 *
 * Deliberately NOT one click for two thousand accounts. It works in batches of 200, says
 * how many are left after each, and stops where it is if something goes wrong — a mistake
 * is then 200 accounts to archive rather than every customer on the books.
 *
 * A customer is skipped when their phone already belongs to a user, which is the same
 * identity rule the quick-add uses. Running this twice therefore creates nothing the
 * second time.
 *
 * Super Admin and Accountant only; the server enforces it, this just hides the button.
 */

const FONT = "'Poppins', sans-serif";

/* 100, not 200.
   (user 2026-11-02: "why 200 actually")

   Two reasons, and the second one bit. A smaller batch means a mistake is a smaller
   mess to archive — that was the original thinking. It also has to FINISH inside the
   request timeout, and 200 did not: on prod each account was re-reading the whole user
   table to check the phone, so the second batch ran past the proxy's limit. The work
   completed and the browser never heard, which is the worst shape a failure can take.
   The scan is fixed, and this is halved on top so there is room to spare. */
const BATCH = 100;

export default function AddReferrersFromCustomersDialog({ open, onClose, onDone }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [total, setTotal] = useState(0);
    const [skippedCompanies, setSkippedCompanies] = useState(0);
    const [sample, setSample] = useState([]);
    const [busy, setBusy] = useState(false);
    const [createdSoFar, setCreatedSoFar] = useState(0);

    const look = useCallback(() => {
        setLoading(true);
        setError('');
        getCustomerCandidates()
            .then(res => {
                setTotal(res?.data?.total || 0);
                setSkippedCompanies(res?.data?.skippedCompanies || 0);
                setSample(Array.isArray(res?.data?.sample) ? res.data.sample : []);
            })
            .catch(() => setError('Could not work out which customers are missing an account.'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { if (open) { setCreatedSoFar(0); look(); } }, [open, look]);

    const runBatch = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await createReferrersFromCustomers(BATCH);
            const made = res?.data?.created || 0;
            setCreatedSoFar(n => n + made);
            setTotal(res?.data?.remaining ?? 0);
            if (onDone) onDone();
        } catch (e) {
            /* DO NOT claim nothing happened. A timeout here means the reply was lost, not
               that the work was undone — on prod a "failed" batch had in fact created all
               200. So re-count from the server and say what is actually true now.
               (2026-11-02) */
            try {
                const again = await getCustomerCandidates();
                const left = again?.data?.total ?? 0;
                const done = Math.max(0, total - left);
                setCreatedSoFar(n => n + done);
                setTotal(left);
                setError(done > 0
                    ? `The reply timed out, but ${done} were created. ${left} left.`
                    : 'That batch did not get through. Nothing was created — try again.');
                if (onDone) onDone();
            } catch {
                setError(e?.response?.data?.message
                    || 'That batch stopped partway. Reopen this box to see where it got to.');
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontFamily: FONT, fontWeight: 700 }}>Add referrers from customers</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
                ) : (
                    <Box>
                        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

                        <Typography sx={{ fontFamily: FONT, fontSize: '0.9rem', color: '#0F172A', mb: 1 }}>
                            <b>{total}</b> customer{total === 1 ? '' : 's'} on an active sale
                            {total > 0 ? ' with a phone number and no account yet.' : ' are left — everyone already has one.'}
                        </Typography>

                        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', mb: 1 }}>
                            Only active sales count, the same door new customers come through
                            automatically. A deal that never completed leaves no referrer behind.
                        </Typography>

                        <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
                            Each one gets a Referral account at 2%, filed under the agent on their most
                            recent sale, with no password set — use “Send login” when you want them in.
                            Anyone whose number already belongs to a user is skipped, so running this
                            again creates nothing twice.
                        </Typography>

                        {skippedCompanies > 0 && (
                            <Typography sx={{ fontFamily: FONT, fontSize: '0.8rem', color: '#64748B', mb: 2 }}>
                                <b>{skippedCompanies}</b> compan{skippedCompanies === 1 ? 'y is' : 'ies are'} left
                                out — a referral fee is paid to a person against an IC, not to an
                                accounts department. Add one by hand if you want it.
                            </Typography>
                        )}

                        {sample.length > 0 && (
                            <Box sx={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 2, p: 1.5, mb: 2 }}>
                                <Typography sx={{ fontFamily: FONT, fontSize: '0.72rem', fontWeight: 700, color: '#64748B', mb: 0.5 }}>
                                    FIRST FEW
                                </Typography>
                                {sample.map(s => (
                                    <Typography key={s.customerId} sx={{ fontFamily: FONT, fontSize: '0.78rem', color: '#374151' }}>
                                        {s.name} · {s.phone}
                                    </Typography>
                                ))}
                            </Box>
                        )}

                        {createdSoFar > 0 && (
                            <Alert severity="success" sx={{ mb: 1 }}>
                                {createdSoFar} referrer{createdSoFar === 1 ? '' : 's'} created so far.
                            </Alert>
                        )}
                        {busy && <LinearProgress sx={{ borderRadius: 1 }} />}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button onClick={onClose} disabled={busy} sx={{ fontFamily: FONT, textTransform: 'none' }}>
                    Close
                </Button>
                <Button
                    onClick={runBatch}
                    disabled={busy || loading || total === 0}
                    variant="contained"
                    sx={{ fontFamily: FONT, textTransform: 'none', fontWeight: 600 }}
                >
                    {busy ? 'Creating…' : `Create next ${Math.min(BATCH, total)}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
